import { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STORAGE_KEY = "cb_notified_bookings";
const CHECK_INTERVAL = 60_000;
const REMINDER_HOURS = 2;

function getNotified() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch (e) {
    if (import.meta.env.DEV) console.warn("failed to read booking reminders storage:", e);
    return new Set();
  }
}

function markNotified(ids) {
  try {
    const set = getNotified();
    ids.forEach((id) => set.add(id));
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch (e) {
    if (import.meta.env.DEV) console.warn("failed to write booking reminders storage:", e);
  }
}

export default function useBookingReminders() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const intervalRef = useRef(null);

  const check = useCallback(async () => {
    if (!user) return;

    try {
      const r = await api.get("/bookings");
      const bookings = r.data || [];
      const now = new Date();
      const notified = getNotified();
      const toNotify = [];

      for (const b of bookings) {
        if (b.status !== "confirmed") continue;
        if (notified.has(b.id)) continue;

        const bookingDate = new Date(b.date + "T" + String(b.start_hour).padStart(2, "0") + ":00:00");
        const diffMs = bookingDate.getTime() - now.getTime();
        const diffHours = diffMs / (1000 * 60 * 60);

        if (diffHours > 0 && diffHours <= REMINDER_HOURS) {
          toNotify.push(b);
        }
      }

      if (toNotify.length === 0) return;

      const ids = toNotify.map((b) => b.id);
      markNotified(ids);

      for (const b of toNotify) {
        const body = t("booking_reminder.body", {
          court: b.court_name,
          time: `${String(b.start_hour).padStart(2, "0")}:00`,
        });

        toast(body, {
          icon: "🛎️",
          duration: 8_000,
          action: {
            label: t("booking_reminder.view"),
            onClick: () => window.location.assign(`/bookings/${b.id}`),
          },
        });

        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("GoCaptain", {
            body,
            icon: "/favicon.ico",
          });
        }
      }
    } catch (e) {
      if (import.meta.env.DEV) console.warn("booking reminders check failed:", e);
    }
  }, [user, t]);

  useEffect(() => {
    if (!user) return;

    check();
    intervalRef.current = setInterval(check, CHECK_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, check]);
}
